import { Badge } from "@/components/ui/badge";
import { Bot, ShoppingCart, MapPin, CreditCard, Check } from "lucide-react";

const STATE_LABELS = {
  greeting: "Cumprimentando",
  browsing: "Navegando cardapio",
  cart_building: "Montando pedido",
  delivery_choice: "Escolhendo entrega",
  address_input: "Informando endereco",
  order_review: "Revisando pedido",
  payment_pending: "Aguardando pagamento",
  completed: "Concluido",
};

const STATE_COLORS = {
  greeting: "bg-blue-100 text-blue-700",
  browsing: "bg-indigo-100 text-indigo-700",
  cart_building: "bg-amber-100 text-amber-700",
  delivery_choice: "bg-purple-100 text-purple-700",
  address_input: "bg-purple-100 text-purple-700",
  order_review: "bg-orange-100 text-orange-700",
  payment_pending: "bg-emerald-100 text-emerald-700",
  completed: "bg-gray-100 text-gray-700",
};

export default function AISessionPanel({ session }) {
  if (!session || !session.id) return null;

  const cart = session.cart || [];
  const total = cart.reduce((s, i) => s + i.quantity * i.unit_price, 0);
  const state = session.state || "idle";

  return (
    <div className="w-64 bg-white border-l flex flex-col flex-shrink-0 overflow-y-auto" data-testid="ai-session-panel">
      <div className="p-3 border-b bg-gradient-to-r from-indigo-50 to-purple-50">
        <div className="flex items-center gap-2 mb-2">
          <Bot className="w-4 h-4 text-indigo-600" />
          <span className="text-xs font-bold text-indigo-800 uppercase tracking-wide">IA Autonoma</span>
        </div>
        <Badge className={`text-[10px] ${STATE_COLORS[state] || "bg-gray-100 text-gray-700"}`} data-testid="session-state-badge">
          {STATE_LABELS[state] || state}
        </Badge>
      </div>

      {cart.length > 0 && (
        <div className="p-3 border-b">
          <div className="flex items-center gap-1.5 mb-2">
            <ShoppingCart className="w-3.5 h-3.5 text-amber-600" />
            <span className="text-xs font-bold text-gray-700">Carrinho ({cart.length})</span>
          </div>
          <div className="space-y-1.5">
            {cart.map((item, i) => (
              <div key={i} className="flex justify-between text-xs" data-testid={`cart-item-${i}`}>
                <span className="text-gray-600 truncate flex-1">{item.quantity}x {item.product_name}</span>
                <span className="font-medium text-gray-800 ml-2">{(item.quantity * item.unit_price).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="mt-2 pt-2 border-t flex justify-between">
            <span className="text-xs font-bold text-gray-700">Total</span>
            <span className="text-sm font-bold text-emerald-700">{total.toFixed(2)}</span>
          </div>
        </div>
      )}

      {session.delivery_type && (
        <div className="p-3 border-b">
          <div className="flex items-center gap-1.5 mb-1">
            <MapPin className="w-3.5 h-3.5 text-blue-600" />
            <span className="text-xs font-bold text-gray-700">
              {session.delivery_type === "delivery" ? "Entrega" : "Retirada"}
            </span>
          </div>
          {session.delivery_address && (
            <p className="text-xs text-gray-500 mt-1">{session.delivery_address}</p>
          )}
        </div>
      )}

      {session.order_id && (
        <div className="p-3 border-b">
          <div className="flex items-center gap-1.5 mb-1">
            <Check className="w-3.5 h-3.5 text-emerald-600" />
            <span className="text-xs font-bold text-gray-700">Pedido Criado</span>
          </div>
          <p className="text-xs text-gray-500 font-mono">#{session.order_id.substring(0, 8)}</p>
        </div>
      )}

      {session.payment_link && (
        <div className="p-3 border-b">
          <div className="flex items-center gap-1.5 mb-1">
            <CreditCard className="w-3.5 h-3.5 text-green-600" />
            <span className="text-xs font-bold text-gray-700">Link Pagamento</span>
          </div>
          <a href={session.payment_link} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 underline break-all">
            {session.payment_link.substring(0, 40)}...
          </a>
        </div>
      )}

      {cart.length === 0 && !session.order_id && (
        <div className="p-4 text-center">
          <p className="text-xs text-gray-400">A IA esta atendendo este cliente automaticamente.</p>
        </div>
      )}
    </div>
  );
}
